import * as React from 'react';
import ActionBar from '../common/ActionBar';
import OptionsMenu from '../common/OptionsMenu';
import ProductsLookUpDrawer from '../common/SearchLookUpDrawer/Containers/Products';
import { getModifiedModuleTree, shallowCompare } from '../../utils/utils';

import { withRouter, Route } from 'react-router-dom';
import BreadcrumbBar from '../common/BreadcrumbBar';
import * as styles from './InventoryEnquiry.scss';
import * as options from './OptionItems.json';
import { Operation } from '../../utils/operations';
import ProductDetails from './ProductDetails';
import Associations from './Associations';
import WhereUsed from './WhereUsed';
import Pricing from './Pricing';
import Sales from './Sales';
import Stocking from './Stocking';
import Attachments from './Attachments';
import { IInventoryEnquiryViewProperties, IInventoryEnquiryViewState } from './interfaces';
import { MODULE_TREE, NON_EXISTING_MODULES } from './constants';
import filterOptions from './InventoryEnquiry.filterRow';
import ProductLookup from 'components/common/ProductLookup';
import Dashboard from 'components/common/Dashboard';
import Reordering from './Reordering';
import Quantities from './Quantities';

const inlineStyles = {
  inventoryEnquirySection: {
    width: 'calc(100% - 20px)',
    marginLeft: '10px',
  }
};

class InventoryEnquiryView extends React.Component<IInventoryEnquiryViewProperties, IInventoryEnquiryViewState> {

  constructor(props: Readonly<IInventoryEnquiryViewProperties>) {
    super(props);
    this.state = {
      productLookupValue: {}
    };
  }

  getDefaultTab = (): string => {
    const { isV3 } = this.props;

    return isV3 ? 'Dashboard' : 'ProductDetails';
  }

  handleModuleChange = (selectedTab: string, searchParamOverrides?: Record<string, string>): void => {
    const { isV3 } = this.props;
    const params = (new URLSearchParams(this.props.location.search));
    if (!params.get('ProductId') && (isV3 ? selectedTab !== 'Dashboard' : selectedTab !== 'ProductDetails')) {
      this.props.changeSelectedTab(this.getDefaultTab());

      return;
    }
    if (selectedTab === 'Balances' && !!this.props.selectedCustomer) {
      if (this.props.fetchBalanceDetails) this.props.fetchBalanceDetails({ CustomerId: this.props.selectedCustomer.CustomerId });
    }

    const fromNonExisting = NON_EXISTING_MODULES.find((node) => node.id === selectedTab);
    const targetTab = (fromNonExisting && fromNonExisting.target) || selectedTab;
    this.props.changeSelectedTab(targetTab);

    const context = 'inventory-enquiry';
    this.props.getFormSchema({
      context,
      formId: targetTab
    });
    this.props.changeSelectedForm(context, targetTab);
    this.props.changeOperationMode(Operation.BROWSE);

    const routeObj = this.getRoute()[targetTab || this.getDefaultTab()];

    if (searchParamOverrides && routeObj) {
      const p = new URLSearchParams(routeObj.search);
      Object.entries(searchParamOverrides).forEach(([key, value]) => {
        if (value != null) p.set(key, value);
      });
      routeObj.search = `?${p.toString()}`;
    }

    this.props.history.push(routeObj);
    this.fetchChildRecords(targetTab);
  }

  getRoute = (): { [name: string]: { pathname: string; search: string; component: any; props?: any; fetchChildRecords?(params: any): void } } => {
    const context = 'inventory-enquiry';
    const params = (new URLSearchParams(this.props.location.search));
    const ProductId = params.get('ProductId') || '';
    const LotSerial = params.get('LotSerial') || '';
    const Warehouse = params.get('Warehouse') || '';

    const routes = {
      Dashboard: { pathname: `/${context}/dashboard`, search: `?ProductId=${ProductId}`, component: this.props.isV3 ? Dashboard : ProductDetails, props: { programId: 'inventory-enquiry' } },
      InventoryEnquiry: { pathname: `/${context}`, search: `?ProductId=${ProductId}`, component: this.props.isV3 ? Dashboard : ProductDetails, props: { programId: 'inventory-enquiry' } },
      ProductDetails: { pathname: `/${context}/product-details`, search: `?ProductId=${ProductId}`, component: ProductDetails, fetchChildRecords: this.fetchProductDetails },
      HistoricalDates: { pathname: `/${context}/product-details/historical-dates`, search: `?ProductId=${ProductId}`, component: ProductDetails },
      Notepad: { pathname: `/${context}/product-details/notepad`, search: `?ProductId=${ProductId}`, component: ProductDetails },
      UserFields: { pathname: `/${context}/product-details/user-fields`, search: `?ProductId=${ProductId}`, component: ProductDetails },
      AssociationsAlternates: { pathname: `/${context}/associations/alternates`, search: `?ProductId=${ProductId}`, component: Associations },
      AssociationsSupersessions: { pathname: `/${context}/associations/suppressions`, search: `?ProductId=${ProductId}`, component: Associations, },
      AssociationsBillOfMaterials: { pathname: `/${context}/associations/bill-of-material`, search: `?ProductId=${ProductId}`, component: Associations },
      WhereUsedBOM: { pathname: `/${context}/where-used/bill-of-material`, search: `?ProductId=${ProductId}`, component: WhereUsed },
      WhereUsedCatalogue: { pathname: `/${context}/where-used/catalogue`, search: `?ProductId=${ProductId}`, component: WhereUsed, },
      Prices: { pathname: `/${context}/pricing/prices`, search: `?ProductId=${ProductId}`, component: Pricing },
      ForeignPrices: { pathname: `/${context}/pricing/foreign-prices`, search: `?ProductId=${ProductId}`, component: Pricing, },
      PriceMatrix: { pathname: `/${context}/pricing/price-matrix`, search: `?ProductId=${ProductId}`, component: Pricing, },
      Schedule: { pathname: `/${context}/pricing/schedule`, search: `?ProductId=${ProductId}`, component: Pricing, },
      FutureBreaks: { pathname: `/${context}/pricing/future-breaks`, search: `?ProductId=${ProductId}`, component: Pricing },
      PriceBreaks: { pathname: `/${context}/pricing/price-breaks`, search: `?ProductId=${ProductId}`, component: Pricing },
      IESSalesOrders: { pathname: `/${context}/sales/sales-orders`, search: `?ProductId=${ProductId}`, component: Sales, fetchChildRecords: this.setSalesOrdersFilters },
      ServiceOrders: { pathname: `/${context}/sales/service-orders`, search: `?ProductId=${ProductId}`, component: Sales, fetchChildRecords: this.setServiceOrdersFilters },
      IESReturnAuthorities: { pathname: `/${context}/sales/return-authorities`, search: `?ProductId=${ProductId}`, component: Sales, fetchChildRecords: this.setAuthorizedFilters },
      IESLostSales: { pathname: `/${context}/sales/lost-sales`, search: `?ProductId=${ProductId}`, component: Sales, fetchChildRecords: this.setLostSalesFilters },
      IESQuotes: { pathname: `/${context}/sales/quotes`, search: `?ProductId=${ProductId}`, component: Sales, fetchChildRecords: this.fetchSalesQuotes },
      IEPurchaseOrders: { pathname: `/${context}/reordering/purchase-orders`, search: `?ProductId=${ProductId}`, component: Reordering, fetchChildRecords: this.setPurchaseOrdersFilters },
      IESuppliers: { pathname: `/${context}/reordering/suppliers`, search: `?ProductId=${ProductId}`, component: Reordering },
      IESettingsByWarehouse: { pathname: `/${context}/reordering/settings-by-warehouse`, search: `?ProductId=${ProductId}`, component: Reordering },
      Stocking: { pathname: `/${context}/stocking`, search: `?ProductId=${ProductId}`, component: Stocking },
      StockingQuantities: { pathname: `/${context}/stocking/quantities`, search: `?ProductId=${ProductId}`, component: Quantities },
      StockingReserveHistory: { pathname: `/${context}/stocking/reserve-history`, search: `?ProductId=${ProductId}`, component: Stocking, fetchChildRecords: this.setReverseHistoryFilters },
      StockingBinLocations: { pathname: `/${context}/stocking/bin-locations`, search: `?ProductId=${ProductId}`, component: Stocking, fetchChildRecords: this.setBinLocationFilters },
      StockingMovements: { pathname: `/${context}/stocking/movements`, search: `?ProductId=${ProductId}`, component: Stocking, fetchChildRecords: this.fetchStockingMovements },
      StockingSerials: { pathname: `/${context}/stocking/serial-numbers`, search: `?ProductId=${ProductId}`, component: Stocking, fetchChildRecords: this.setSerialNumberFilters },
      StockingLotDetails: { pathname: `/${context}/stocking/lot-details`, search: `?ProductId=${ProductId}`, component: Stocking, fetchChildRecords: this.setLotDetailsFilters },
      StockingLotPicking: { pathname: `/${context}/stocking/lot-details/allocation-details/picking`, search: `?ProductId=${ProductId}&LotSerial=${LotSerial}&Warehouse=${Warehouse}`, component: Stocking },
      StockingLotReserved: { pathname: `/${context}/stocking/lot-details/allocation-details/reserved`, search: `?ProductId=${ProductId}&LotSerial=${LotSerial}&Warehouse=${Warehouse}`, component: Stocking, },
      StockingLotInPutAway: { pathname: `/${context}/stocking/lot-details/allocation-details/in-put-away`, search: `?ProductId=${ProductId}&LotSerial=${LotSerial}&Warehouse=${Warehouse}`, component: Stocking, },
      StockingQuantitiesPurchaseOrder: { pathname: `/${context}/stocking/quantities/details/orders/purchase-orders`, search: `?ProductId=${ProductId}&Warehouse=${Warehouse}`, component: Stocking },
      StockingQuantitiesProductionOrder: { pathname: `/${context}/stocking/quantities/details/orders/production-orders`, search: `?ProductId=${ProductId}&Warehouse=${Warehouse}`, component: Stocking },
      StockingQuantitiesInTransitImport: { pathname: `/${context}/stocking/quantities/details/orders/in-transit-import`, search: `?ProductId=${ProductId}&Warehouse=${Warehouse}`, component: Stocking, },
      StockingQuantitiesInTransitLocal: { pathname: `/${context}/stocking/quantities/details/orders/in-transit-local`, search: `?ProductId=${ProductId}&Warehouse=${Warehouse}`, component: Stocking },
      StockingQuantitiesPicking: { pathname: `/${context}/stocking/quantities/details/sales/picking`, search: `?ProductId=${ProductId}&Warehouse=${Warehouse}`, component: Stocking },
      StockingQuantitiesKitting: { pathname: `/${context}/stocking/quantities/details/sales/kitting`, search: `?ProductId=${ProductId}&Warehouse=${Warehouse}`, component: Stocking },
      StockingQuantitiesReserved: { pathname: `/${context}/stocking/quantities/details/sales/reserved`, search: `?ProductId=${ProductId}&Warehouse=${Warehouse}`, component: Stocking, },
      StockingQuantitiesServiceWIP: { pathname: `/${context}/stocking/quantities/details/work-in-progress/service`, search: `?ProductId=${ProductId}&Warehouse=${Warehouse}`, component: Stocking },
      StockingQuantitiesProductionWIP: { pathname: `/${context}/stocking/quantities/details/work-in-progress/production`, search: `?ProductId=${ProductId}&Warehouse=${Warehouse}`, component: Stocking },
      StockingQuantitiesInPutAwayWIP: { pathname: `/${context}/stocking/quantities/details/work-in-progress/in-put-away`, search: `?ProductId=${ProductId}&Warehouse=${Warehouse}`, component: Stocking },
      Documents: { pathname: `/${context}/attachments/documents`, search: `?ProductId=${ProductId}`, component: Attachments },
      Links: { pathname: `/${context}/attachments/links`, search: `?ProductId=${ProductId}`, component: Attachments },
      Images: { pathname: `/${context}/attachments/images`, search: `?ProductId=${ProductId}`, component: Attachments, fetchChildRecords: this.getAttachmentsImages },
    };

    return routes;
  }

  handleToggleMenuOptionOpen = (): void => {
    this.props.toggleMenuOption(!this.props.isMenuOptionOpen);
  }

  componentDidMount(): void {
    const { staff, setWarehouse } = this.props;
    if (staff) setWarehouse(staff.defaultWarehouseEntity);
    this.props.changeOperationMode(Operation.BROWSE);
  }

  shouldComponentUpdate(nextProps: Readonly<IInventoryEnquiryViewProperties>): boolean {
    return shallowCompare(this, nextProps);
  }

  componentDidUpdate(prevProps: IInventoryEnquiryViewProperties): void {
    const { operationMode, selectedTab, selectedProduct, productDetailsSummary } = this.props;

    if (operationMode === Operation.BACK) {
      this.backAction(prevProps);
    }
    if (!this.props.selectedForm) {
      this.props.changeSelectedForm('inventory-enquiry', this.props.selectedTab);
    }
    if (selectedTab !== prevProps.selectedTab) {
      this.handleModuleChange(selectedTab);
    }

    if (selectedProduct && !productDetailsSummary && selectedTab !== 'Dashboard') {
      this.fetchProductSummary();
    }

    if (selectedProduct && productDetailsSummary) {
      if (selectedProduct.ProductId.toString() !== productDetailsSummary.ProductId.toString()) {
        this.fetchProductSummary();
      }
    }

    if (selectedTab === 'Prices' && selectedProduct?.ProductId) this.props.fetchPricesSummary({ ProductId: selectedProduct.ProductId, Warehouse: this.props.staff.defaultWarehouseEntity });
  }

  componentWillUnmount(): void {
    this.props.resetSearchLookupDrawer();
    this.props.resetproductDetailsModule();
    this.props.destoryForm();
  }

  cancelFormSubmission = (): void => {
    const { dirty } = this.props;
    if (dirty) {
      this.props.changeConfirmationDialog({
        open: true,
        title: 'Discard changes',
        message: 'Are you sure you want to continue?',
        okLabel: 'Discard',
        onCancel: () => {
          this.props.changeOperationMode(Operation.EDIT);
        },
        onOk: () => {
          this.props.resetForm();
          this.props.changeOperationMode(Operation.BROWSE);
        }
      });
    } else {
      this.props.changeOperationMode(Operation.BROWSE);
    }
  }

  backAction = (prevProps: IInventoryEnquiryViewProperties) => {
    const { dirty, selectedTab } = this.props;
    if (dirty) {
      this.props.changeConfirmationDialog({
        open: true,
        title: 'Discard changes',
        message: 'Are you sure you want to continue?',
        okLabel: 'Discard',
        onCancel: () => {
          this.props.changeOperationMode(prevProps.operationMode);
        },
        onOk: () => {
          if (selectedTab === 'ProductDetails') {
            this.props.history.push('/dashboard');
          }
          this.handleModuleChange('ProductDetails');
          this.props.changeOperationMode(Operation.BROWSE);
          this.props.resetForm();
        }
      });
    } else {
      if (selectedTab) {
        if (selectedTab === 'ProductDetails') {
          this.props.history.push('/dashboard');
        } else {
          this.handleModuleChange(this.getDefaultTab());
        }
      }
      this.props.changeOperationMode(Operation.BROWSE);
    }
  }

  fetchChildRecords = (selectedTab: string): void => {
    const routeData = this.getRoute()[selectedTab];
    if (routeData && routeData.fetchChildRecords) {
      routeData.fetchChildRecords(null);
    }
  }

  fetchStockingMovements = (): void => {
    const { staff } = this.props;
    if (staff) this.props.setStockingMovementsWarehouse(staff.defaultWarehouseEntity);
  }

  getAttachmentsImages = (): void => {
    const { getAttachmentsImages, selectedProduct, location } = this.props;
    const params = new URLSearchParams(location.search);
    const ProductId = selectedProduct ? selectedProduct.ProductId : params.get('ProductId') ? parseInt(params.get('ProductId')) : '';
    if (ProductId) {
      getAttachmentsImages({ ProductId });
    }
  }

  setBinLocationFilters = (): void => {
    const { staff, setStockingBinLocationsWarehouse } = this.props;
    if (staff) {
      setStockingBinLocationsWarehouse(staff.defaultWarehouseEntity);
    }
  }

  setLostSalesFilters = (): void => {
    const { staff, setLostSalesWarehouse } = this.props;
    if (staff) {
      setLostSalesWarehouse(staff.defaultWarehouseEntity);
    }
  }

  setAuthorizedFilters = (): void => {
    const { staff, setReturnAuthoritiesEntityView } = this.props;
    if (staff) {
      setReturnAuthoritiesEntityView(staff.defaultView, staff.defaultWarehouseEntity);
    }
  }

  setLotDetailsFilters = (): void => {
    const { staff, setStockingLotDetailsWarehouse } = this.props;
    if (staff) {
      setStockingLotDetailsWarehouse(staff.defaultWarehouseEntity);
    }
  }

  setReverseHistoryFilters = (): void => {
    const { staff, setStockingReserveHistoryWarehouse } = this.props;
    if (staff) {
      setStockingReserveHistoryWarehouse(staff.defaultWarehouseEntity);
    }
  }

  setSerialNumberFilters = (): void => {
    const { staff, setStockingSerialsWarehouse } = this.props;
    if (staff) {
      setStockingSerialsWarehouse(staff.defaultWarehouseEntity);
    }
  }

  setServiceOrdersFilters = (): void => {
    const { staff, setServiceOrderEntityView } = this.props;
    if (staff) {
      setServiceOrderEntityView(staff.defaultView, staff.defaultWarehouseEntity);
    }
  }

  setSalesOrdersFilters = (): void => {
    const { staff, setSalesOrderEntityView } = this.props;
    if (staff) {
      setSalesOrderEntityView(staff.defaultView, staff.defaultWarehouseEntity);
    }
  }

  setPurchaseOrdersFilters = (): void => {
    const { staff, setPurchaseOrdersEntityView } = this.props;
    if (staff) {
      setPurchaseOrdersEntityView(staff.defaultView, staff.defaultWarehouseEntity);
    }
  }

  setQuotesFilters = (): void => {
    const { staff, setStockingBinLocationsWarehouse } = this.props;
    if (staff) {
      setStockingBinLocationsWarehouse(staff.defaultWarehouseEntity);
    }
  }

  fetchSalesQuotes = (): void => {
    const { staff } = this.props;
    const { setSalesQuotesEntityView } = this.props;
    if (staff) setSalesQuotesEntityView(staff.defaultView, staff.defaultWarehouseEntity);
  }

  fetchProductDetails = (): void => {
    const { fetchProductDetails, fetchProductSummary, selectedProduct } = this.props;
    if (selectedProduct && selectedProduct.ProductId) {
      fetchProductDetails({
        ProductId: selectedProduct && selectedProduct.ProductId
      });
      fetchProductSummary({
        ProductId: selectedProduct && selectedProduct.ProductId
      });
    }

  }

  fetchProductSummary = () => {
    const { selectedProduct } = this.props;
    if (selectedProduct && selectedProduct.ProductId) {
      this.props.fetchProductSummary({ ProductId: selectedProduct && selectedProduct.ProductId });
    }
  }

  getOptionMenuData = (selectedTab: string) => {
    switch (selectedTab) {
      case 'StockingQuantitiesPurchaseOrder':
      case 'StockingQuantitiesProductionOrder':
      case 'StockingQuantitiesInTransitImport':
      case 'StockingQuantitiesInTransitLocal':
      case 'StockingQuantitiesPicking':
      case 'StockingQuantitiesReserved':
      case 'StockingQuantitiesKitting':
      case 'StockingQuantitiesServiceWIP':
      case 'StockingQuantitiesProductionWIP':
      case 'StockingQuantitiesInPutAwayWIP':
      case 'StockingQuantitiesDetails': return options.QuantitiesDetails;
      case 'StockingLotInPutAway':
      case 'StockingLotReserved':
      case 'StockingLotPicking': return options.StockingLotDetails;
      default: return options.InventoryEnquiry;
    }
  }

  resetChildGrids = () => {
    const { resetStockingSerials } = this.props;
    resetStockingSerials();
  }

  onProductChange = (): void => {
    const { selectedTab } = this.props;
    this.fetchChildRecords(selectedTab);
  }

  render(): React.ReactNode {
    const {
      selectedTab, isMenuOptionOpen, setLookupData, defaultWarehouse } = this.props;
    const routes = Object.values(this.getRoute());

    return (
      <div className={styles.inventoryEnquiryOuter}>
        <div className={styles.inventoryEnquiryContainer}>
          <ProductsLookUpDrawer
            enableToggle={true}
            recordChangeCallBacks={[
              this.onProductChange,
              this.resetChildGrids
            ]}
            customFilterRow={
              { ...filterOptions, parameters: filterOptions?.parameters as any, localUpdaterParams: ['ProductFrom', 'ProductTo'] }
            }
            initialValues={{
              Active: true
            }}
            customSearch={
              <ProductLookup
                Warehouse={defaultWarehouse}
                size={'medium'}
                placeholder={'Search for products...'}
                LookupLabel=''
                suppressDescription={true}
                value=''
                display={this.state?.productLookupValue?.Display || ''}
                onSelectedItemChange={(item: any, _) => {
                  this.setState({ productLookupValue: item });
                  if (item) {
                    setLookupData(item);
                    this.setState({ productLookupValue: {} });
                  }
                }
                }
              />
            }
          />
          <div
            style={inlineStyles.inventoryEnquirySection}
            className={styles.inventoryEnquirySection}
          >
            <BreadcrumbBar
              onClick={this.handleToggleMenuOptionOpen}
              mainModule={'Inventory Enquiry'}
              activeModule={selectedTab}
              moduleTree={getModifiedModuleTree(MODULE_TREE, this.props.isV3, 'Dashboard', 'ProductDetails')}
            />
            {routes.map((route) => (
              <Route
                exact
                key={`${route.pathname}-${route.props?.programId || ''}-${route.search}`}
                path={route.pathname}
                render={() => (
                  <route.component
                    onInitialLoad={this.handleModuleChange}
                    path={route.pathname}
                    formName={`${selectedTab}`}
                    {...(route.props || {})}
                  />
                )}
              />
            ))}
          </div>
        </div>
        <OptionsMenu
          open={isMenuOptionOpen}
          options={this.getOptionMenuData(selectedTab)}
          onOptionClick={this.handleModuleChange}
          defaultValue={selectedTab}
        />
        <ActionBar scope={'inventory-enquiry'} />
      </div>
    );
  }
}

export default withRouter(InventoryEnquiryView);
